import { Criticidad } from '../domain/value-objects/Criticidad';

/**
 * Frecuencias del aviso, según lo grave que sea.
 *
 * Más pitidos y más agudos cuanto más grave: quien está en otra pestaña
 * distingue una alarma crítica de una menor sin mirar la pantalla.
 */
const TONOS: Readonly<Record<string, readonly number[]>> = {
  critica: [880, 1175, 880, 1175],
  alta: [880, 1175],
  media: [740, 988],
  baja: [660, 880],
};

const DURACION_PITIDO_S = 0.16;
const SILENCIO_S = 0.07;
const VOLUMEN = 0.35;

/** Sin esta pausa, una ráfaga de alarmas se convierte en ruido continuo. */
const ESPERA_ENTRE_AVISOS_MS = 1500;

/**
 * Aviso sonoro de alarmas nuevas.
 *
 * El sonido se sintetiza en el momento en vez de reproducir un archivo: no hay
 * nada que descargar y el tono puede depender de la gravedad.
 *
 * Los navegadores no dejan sonar nada hasta que la persona interactúa con la
 * página, así que el permiso se toma en la primera pulsación o tecla y a
 * partir de ahí el aviso funciona esté donde esté el usuario.
 */
export class ServicioAvisoSonoro {
  private contexto: AudioContext | null = null;
  private ultimoAviso = 0;
  private desbloqueando = false;

  /** Prepara el audio y queda a la espera del primer gesto del usuario. */
  preparar(): void {
    if (this.desbloqueando) return;
    this.desbloqueando = true;

    const desbloquear = () => {
      this.asegurarContexto();
      void this.contexto?.resume().catch(() => undefined);
    };

    // `once` basta: en cuanto el navegador acepta, el permiso se mantiene.
    window.addEventListener('pointerdown', desbloquear, { once: true });
    window.addEventListener('keydown', desbloquear, { once: true });
  }

  /** Suena si toca; devuelve `false` si se descartó por venir muy seguido. */
  avisar(criticidad: Criticidad): boolean {
    const ahora = Date.now();
    if (ahora - this.ultimoAviso < ESPERA_ENTRE_AVISOS_MS) return false;

    this.ultimoAviso = ahora;
    return this.reproducir(criticidad.valor);
  }

  /** Suena sin respetar la pausa; para el botón de prueba. */
  probar(criticidad: Criticidad): boolean {
    this.ultimoAviso = 0;
    return this.avisar(criticidad);
  }

  private reproducir(nivel: string): boolean {
    this.asegurarContexto();
    const contexto = this.contexto;
    if (!contexto) return false;

    // Todavía sin permiso del navegador: se pide y este aviso se pierde.
    if (contexto.state === 'suspended') {
      void contexto.resume().catch(() => undefined);
      return false;
    }

    const tonos = TONOS[nivel] ?? TONOS.baja ?? [660, 880];
    const inicio = contexto.currentTime;

    tonos.forEach((frecuencia, indice) => {
      const desde = inicio + indice * (DURACION_PITIDO_S + SILENCIO_S);
      const hasta = desde + DURACION_PITIDO_S;

      const oscilador = contexto.createOscillator();
      const volumen = contexto.createGain();

      oscilador.type = 'sine';
      oscilador.frequency.value = frecuencia;

      // La rampa evita el chasquido de cortar la onda en seco.
      volumen.gain.setValueAtTime(0.0001, desde);
      volumen.gain.exponentialRampToValueAtTime(VOLUMEN, desde + 0.01);
      volumen.gain.exponentialRampToValueAtTime(0.0001, hasta);

      oscilador.connect(volumen).connect(contexto.destination);
      oscilador.start(desde);
      oscilador.stop(hasta + 0.02);
    });

    return true;
  }

  private asegurarContexto(): void {
    if (this.contexto) return;

    const Constructor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!Constructor) return;

    try {
      this.contexto = new Constructor();
    } catch {
      // Sin audio disponible el resto de la aplicación sigue funcionando.
      this.contexto = null;
    }
  }
}

export const servicioAvisoSonoro = new ServicioAvisoSonoro();
